import React, { useEffect, useState } from 'react';
import SquaredImage from '../../SquaredImage/SquaredImage';
import namingPlaceholder from '../../../assets/Naming.png';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { faTrophy, faHeart, faUser } from '@fortawesome/free-solid-svg-icons';

import styles from './DesignCard.module.scss';
import ActionButton, { MultiActionButtonsWrapper } from '../../ActionButton/ActionButton';
import { Contest, GalleryDesign, User } from '../../../global';
import { useNavigate } from 'react-router-dom';
import { registrationDateString } from '../../../helpers/utils';
import { useAuth } from '../../../context/AuthContext';
import { ROUTEPATHS } from '../../../routes/routePaths';
import { briefUrlForContest, designUrlForContestName, designUrlForGallery, userUrlForUser } from '../../../helpers/navigationUtils';

interface Props {
  contest?: Contest;
  design: GalleryDesign;
  user?: User; //if user is passed, it will be used to show detail in the user's portfolio
}

const DesignCard: React.FC<Props> = ({ contest, design, user }) => {
  const redirect = useNavigate();
  const [designName, setDesignName] = useState<string>('');
  const { loggedUser, isDesignLiked, toggleDesignLiked, isGod } = useAuth();

  useEffect(() => {
    if (design) {
      if (design.name) {
        setDesignName(design.name);
      } else {
        if (design.isPrivate || design.hideGalleryTab) {
          setDesignName('Private gallery');
        } else if (design.isHidden) {
          setDesignName('Hidden by author');
        }
      }
    }
  }, [design]);

  const goToDesignDetails = async () => {
    console.log('GO TO DESIGN DETAILS');
    const ownsDesign = loggedUser && (design.username === loggedUser.username || design.idClient === loggedUser.id);
    const canOpenDesign = isGod() || (!design.hideGalleryTab && (!design.isHidden && !design.isPrivate ? true : ownsDesign));

    if (!canOpenDesign) {
      return;
    }

    if (user) {
      redirect(designUrlForGallery(user, design));
    } else {
      redirect(designUrlForContestName(design.contestNameForURL, design));
    }
  };

  const goToUserDetail = async (nameForURL: string) => {
    redirect(userUrlForUser(nameForURL));
  };

  const goToContestDetails = async (contestNameForURL: string) => {
    redirect(briefUrlForContest(contestNameForURL));
  };

  return (
    <div className={`${styles.card} col-12  d-flex flex-row align-items-center justify-content-center position-relative`}>
      <div className={`${styles.cardInfoWrapper} w-sm-75 d-flex flex-column  bg-light`}>
        <div className={`${styles.cardInfoImage} bg-light position-relative ${!design.isHidden && !design.isPrivate && !design.hideGalleryTab ? 'cursor-pointer' : ''}`}>
          <div onClick={() => goToDesignDetails()}>
            {design.isNaming ? (
              <SquaredImage image={design.image} altText={design.name} placeholder={namingPlaceholder} />
            ) : (
              <SquaredImage image={design.image} altText={design.name} />
            )}
          </div>
          {design.isTemporary && (
            <div className={`${styles.draft}`}>
              <span> DRAFT </span>
            </div>
          )}
          {!isDesignLiked(design) && (
            <ActionButton
              label={loggedUser ? 'Save as favorite' : 'Login to save as favorite'}
              color={!loggedUser ? 'light-gray-3' : ''}
              icon="addFavorite"
              position="top-right"
              action={() => toggleDesignLiked(design)}
            />
          )}
          {isDesignLiked(design) && <ActionButton label="Remove from favorites" icon="confirm" position="top-right" color="success" action={() => toggleDesignLiked(design)} />}
          <MultiActionButtonsWrapper position="bottom-left">
            {design.hasAwards && <ActionButton label="award" icon="award" />}
            {design.isPrivate && <ActionButton label="Private gallery" icon="private" />}
            {design.isHidden && <ActionButton label="Hidden by author" icon="private" />}
            {/* {props.accesscode && <ActionButton label="Access code" icon="accesscode" />}
            {props.nda && <ActionButton label="Accept nda" icon="acceptnda" />}
            {props.global && <ActionButton label="Global contest" icon="global" />} */}
          </MultiActionButtonsWrapper>
        </div>
        <div className={`${styles.cardInfoContent} container bg-light py-4`}>
          <div className="row h-25 mb-4 px-2">
            <div className={`col text-center align-self-center `} onClick={() => goToDesignDetails()}>
              <span className={`fw-bold color-blue d-none d-sm-block text-16 ${!design.isHidden && !design.isPrivate && !design.hideGalleryTab ? 'cursor-pointer' : ''}`}>{designName}</span>
              <span className={`fw-bold color-blue d-sm-none d-sm-block text-16 ${!design.isHidden && !design.isPrivate && !design.hideGalleryTab ? 'cursor-pointer' : ''}`}>{designName}</span>
            </div>
          </div>
          {contest && <div className=" col align-self-center text-center  mb-5 ">{contest.contestType}</div>}

          <div className={`${styles.infoContainer} position-static`}>
            <div className=" justify-content-left  mb-3 " style={{ cursor: 'pointer' }} onClick={() => goToUserDetail(design.userNameForURL)}>
              <FontAwesomeIcon icon={faUser as IconProp} className="ps-1 text-left color-blue me-3" />
              <span className=" mx-2 justify-content-left color-blue">{design.username}</span>
            </div>
            <div className="text text-start justify-content-left mb-3 ">
              <FontAwesomeIcon icon={faCalendar as IconProp} className="ps-1 text color-blue me-3" />
              <span className="mx-1  justify-content-left mb-4">Posted: {registrationDateString(design.uploadDate)}</span>
            </div>
            <div
              className="text text-start  justify-content-left mb-3  "
              style={{ height: '20px', overflow: 'hidden', cursor: 'pointer' }}
              onClick={() => goToContestDetails(design.contestNameForURL)}
            >
              <FontAwesomeIcon icon={faTrophy as IconProp} className=" text color-blue me-3" />
              <span className="  justify-content-left  color-blue ">{design.contestName} </span>
            </div>
            <div className="text text-start justify-content-left mb-3 ">
              <FontAwesomeIcon icon={faHeart as IconProp} className="text ps-1 color-blue me-3" />
              <span className="  fw-bold ">
                {design.votesCount} <span className="text "> votes</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignCard;
